import React from 'react';

import { Box, Card, Image, Flex, Text, Loader } from '@opendoor/bricks/core';
import { spaceWithUnits } from '@opendoor/bricks/theme/eero';
import { centsToCurrencyString } from '@opendoor/shared-fe/build/formatUtils';

import {
  FinalOfferContent,
  serviceChargeDataRow,
  estClosingCostDataRow,
  repairCostDataRow,
  coBrokeCommissionRow,
  agentReferralCommissionRow,
  netProceedsDataRow,
} from 'sellDirect/components/closingDash/finalOffer/FinalOfferContent';
import { FinalizedOffer, WithdrawnOffer } from 'sellDirect/OfferContext';
import { ExpirationChip, WithdrawnChip } from 'sellDirect/components/DateChip';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { useAgentReferralPayments } from 'shared/graphql/useAgentReferralPayments';
import { useExperimentationContext } from 'sellDirect/ExperimentationContext';

const OD_BUMPY_LOGO = 'https://imgdrop.imgix.net/604b6fec2e4946a3876628d4739de816.svg';

export interface FinalOfferCardProps {
  offer: FinalizedOffer | WithdrawnOffer;
}

/**
 * Card displaying finalized offer details.
 *
 * storybook-check-ignore
 */
export const FinalOfferCard: React.FC<FinalOfferCardProps> = ({ offer }) => {
  const { sellerInputUUID, showSixPercentFee } = useQualificationContext();
  const {
    eligibleForAgentReferralPayment,
    fetchingAgentReferralPayments,
    agentCommissionCents,
    agentCommissionPercentOfHeadline,
  } = useAgentReferralPayments(sellerInputUUID, offer.shouldSeeAgentExperience);
  const {
    assertions: { isLWCBuyerFallThroughProtectionEnabled },
  } = useExperimentationContext();
  const showNewFee = isLWCBuyerFallThroughProtectionEnabled && showSixPercentFee;

  if (fetchingAgentReferralPayments) {
    return <Loader />;
  }

  return (
    <FinalOfferCardWithoutContext
      offer={offer}
      eligibleForAgentReferralPayment={!!eligibleForAgentReferralPayment}
      agentCommissionCents={agentCommissionCents}
      agentCommissionPercentOfHeadline={agentCommissionPercentOfHeadline}
      showNewFee={showNewFee}
    />
  );
};

export interface FinalOfferCardWithoutContextProps {
  offer: FinalizedOffer | WithdrawnOffer;
  eligibleForAgentReferralPayment: boolean;
  agentCommissionCents: number;
  agentCommissionPercentOfHeadline: number;
  showNewFee: boolean;
}

// component-test-check-ignore
/**
 * Card displaying finalized offer details without context.
 */
export const FinalOfferCardWithoutContext: React.FC<FinalOfferCardWithoutContextProps> = ({
  offer,
  eligibleForAgentReferralPayment,
  agentCommissionCents,
  agentCommissionPercentOfHeadline,
  showNewFee,
}) => {
  const { underwriting } = offer;

  const rowData = [
    serviceChargeDataRow(underwriting, showNewFee),
    estClosingCostDataRow(underwriting),
    repairCostDataRow(offer.shouldSeeAgentExperience, underwriting),
  ];

  // if agent experience & listed, show co-broke commission
  if (offer.shouldSeeAgentExperience && offer.lead.isListed) {
    rowData.push(coBrokeCommissionRow(offer.coBrokePercentage, offer.underwriting));
  }

  rowData.push(netProceedsDataRow(offer.shouldSeeAgentExperience, offer.underwriting));

  // if eligible, show agent referral commission
  if (offer.shouldSeeAgentExperience && agentCommissionPercentOfHeadline != 0) {
    rowData.push(
      agentReferralCommissionRow(
        eligibleForAgentReferralPayment,
        agentCommissionCents,
        agentCommissionPercentOfHeadline,
      ),
    );
  }

  const isExpired = offer.state === 'Finalized' && offer.isExpired;
  const renderHeader = () => (
    <Box width="100%" px={[6, null, 7]}>
      <Flex width="100%" justifyContent="space-between" alignItems="center">
        <Flex alignItems="center" height={spaceWithUnits[8]}>
          <Image src={OD_BUMPY_LOGO} alt="opendoor-bumpy-logo" aria-hidden w="24px" h="24px" />
          <Text
            as="h2"
            ml={4}
            fontWeight="semibold"
            fontSize={['s1', null, 's2']}
            lineHeight={['s1', null, 's2']}
          >
            Finalized Offer
          </Text>
        </Flex>
        {isExpired && (
          <Box data-testid="final-offer-presign-card-Expired-Label">
            <ExpirationChip date={offer.purchaseAgreementExpiresAt.toLocalDate()} />
          </Box>
        )}
        {offer.state === 'Withdrawn' && (
          <Box data-testid="final-offer-presign-card-Withdrawn-Label">
            <WithdrawnChip date={offer.withdrawnAt.toLocalDate()} />
          </Box>
        )}
      </Flex>
      <Text
        fontSize={['s6', null, 's7']}
        lineHeight={['s6', null, 's7']}
        fontWeight="bold"
        mb={4}
        data-testid="final-offer-content-pre-sign-card-Title"
      >
        {centsToCurrencyString(underwriting.finalizedHeadlinePriceCents)}
      </Text>
    </Box>
  );

  return (
    <Card
      role="group"
      aria-label="Finalized offer card"
      variant="primary"
      width="100%"
      data-testid="final-offer-content-pre-sign-card"
      p={[0, null, 0]}
      pb={[4, null, 4]}
      pt={[6, null, 7]}
      backgroundColor={isExpired || offer.state === 'Withdrawn' ? 'neutrals20' : undefined}
    >
      <FinalOfferContent rowData={rowData} renderHeader={renderHeader} />
    </Card>
  );
};
